// Shared Help Centre content — consumed by the Help Centre page, the context-sensitive
// Help Drawer and the Learning dashboard so guide copy lives in exactly one place.
import {
  PlugsConnected, Faders, ListChecks, Sparkle, ShieldCheck, Scales, SealCheck,
  Files, ChartLineUp, UsersThree, Lightbulb, PencilSimpleLine, ChatCircleText,
  Target, Coins, ChartPieSlice, Image, Palette, Rocket, ArrowsClockwise, CalendarCheck,
  Gauge, EnvelopeSimple,
} from "@phosphor-icons/react";

export const SUPPORT_EMAIL = "hello@workwithlottie.com";

export const SECTIONS = [
  {
    tag: "Getting set up",
    guides: [
      {
        icon: PlugsConnected, title: "Connect your Xero organisation", to: "/settings?tab=connect", cta: "Open Connections",
        steps: [
          "Go to Settings → Connect to Xero and click Connect to Xero.",
          "Sign in to Xero and choose the organisation(s) you want Lottie to manage, then authorise access.",
          "You'll be returned to the app with the organisation connected and its chart of accounts imported automatically.",
          "Connecting more than one? Repeat the steps — then switch between them any time using the organisation selector at the top of the sidebar.",
          "You can test or remove a connection at any time from the same screen.",
        ],
        tip: "Lottie only ever connects through Xero's secure login — it never sees or stores your Xero password.",
        detail: {
          walkthrough: [
            "On the Connections screen click “Connect to Xero”. A Xero window opens — sign in with your Xero credentials (not your Lottie ones).",
            "Xero shows a consent screen listing the access Lottie needs (invoices, bank transactions, manual journals and settings). Tick the organisation(s) you want and click “Allow access”.",
            "Lottie pulls in the chart of accounts for each organisation automatically — this can take a few seconds for a large chart.",
            "Back on the Connections screen each organisation shows a green “Connected” status. Use “Test connection” any time to confirm the link is healthy.",
          ],
          gotchas: [
            "If an import later says a source was skipped, your Xero connection is missing a permission — click “Reconnect Xero” to re-authorise and grant it.",
            "Reconnecting never loses your schedules or history; it only refreshes the Xero permissions.",
          ],
        },
      },
      {
        icon: Faders, title: "Tell Lottie which nominal codes to use", to: "/treatment-rules", cta: "Open Treatment Rules",
        steps: [
          "Open Treatment Rules from the sidebar (under Optimisation).",
          "Map each relevant nominal code to its treatment: accrual, prepayment or deferred income.",
          "Set a default release basis (even periods, or daily pro-rata) so schedules can be built automatically.",
          "You can also review and fine-tune code classifications under Settings → Account Codes.",
          "Save — Lottie uses these rules to identify transactions and build schedules going forward.",
        ],
        tip: "Each connected organisation keeps its own independent code mapping, so client charts of accounts never mix.",
        detail: {
          walkthrough: [
            "Add a rule for each nominal code you post accruals, prepayments or deferred income to. Choose the treatment and a default release basis so Lottie can draft schedules without asking every time.",
            "Under Settings → Account Codes you can tag a code with its reporting standard (FRS 102 / FRS 105) so registers and reconciliation group correctly.",
          ],
          gotchas: [
            "Only transactions posted to mapped codes appear in Unscheduled Items — if something's missing, check the code is mapped here first.",
            "Mapping is per-organisation; a code mapped for one client never affects another.",
          ],
        },
      },
      {
        icon: Palette, title: "Add your practice branding", to: "/practice?tab=branding", cta: "Open Branding",
        steps: [
          "Go to Settings → Branding.",
          "Upload your practice logo (it appears on every PDF and Excel export).",
          "Add your practice details under Settings → Practice Details so reports are correctly headed.",
          "Save — exports are instantly client-ready.",
        ],
        tip: "A logo makes register and close-pack exports look polished when they go to clients or partners.",
        detail: {
          walkthrough: [
            "Upload a PNG or JPG logo — it's placed in the header of every PDF and Excel export.",
            "Add your practice name, address and a footer note under Practice Details so reports are correctly headed and disclaimed.",
          ],
          gotchas: [
            "A very wide logo may be scaled down — a square or landscape image around 400px wide works best.",
          ],
        },
      },
    ],
  },
  {
    tag: "Day-to-day",
    guides: [
      {
        icon: ListChecks, title: "Review and schedule transactions", to: "/unscheduled", cta: "Open Unscheduled Items",
        steps: [
          "Open Unscheduled Items to see transactions posted to your mapped codes that don't yet have a schedule.",
          "Open an item to check the amount, period and release basis Lottie suggests.",
          "Adjust the start date, number of months or basis if needed.",
          "Create the schedule — the item moves into the relevant register and its monthly release journals are queued.",
          "Repeat, or bulk-schedule similar items together to clear the list quickly.",
        ],
        tip: "Use the Dashboard's attention badges to see at a glance how many items are waiting to be scheduled.",
        detail: {
          walkthrough: [
            "Click “Import from Xero” to pull the latest transactions on your mapped codes; new items land in Unscheduled Items.",
            "Open an item to see Lottie's suggested period, frequency and release basis. Adjust the start date, number of periods or basis if the invoice covers a different term.",
            "Use “Suggest with AI” to have Lottie read the attached invoice and draft the whole schedule for you.",
            "Create the schedule — the item moves to the relevant register and its release journals are queued as “Pending”.",
          ],
          gotchas: [
            "Each item you open starts from clean defaults — the previous item's settings are never carried over.",
            "Nothing posts to Xero at this stage; journals only post when you run daily posting (or approve them).",
          ],
        },
      },
      {
        icon: PencilSimpleLine, title: "Add a manual schedule or opening balance", to: "/accruals", cta: "Open Accruals",
        steps: [
          "Open the relevant register (Accruals, Prepayments or Deferred Income).",
          "Choose New schedule to create one by hand — useful for opening balances or items that live outside Xero.",
          "Enter the amount, start month, number of periods and release basis.",
          "Save — Lottie applies the same release logic and journals as automated schedules.",
        ],
        tip: "Opening balances entered here let Lottie pick up mid-year so your registers are complete from day one.",
        detail: {
          walkthrough: [
            "Use “New schedule” on any register to add items that aren't in Xero, or to seed an opening balance when you start mid-year.",
            "Enter the amount, start month, number of periods and release basis — Lottie previews the exact journals before you save.",
          ],
          gotchas: [
            "Opening balances make your register complete from day one; without them the register only reflects items imported since you connected.",
          ],
        },
      },
      {
        icon: Sparkle, title: "Set up recurring accruals", to: "/recurring-accruals", cta: "Open Recurring Accruals",
        steps: [
          "Open Recurring Accruals and review Lottie's suggestions, based on repeating bills she's spotted.",
          "Confirm a suggestion to have the accrual posted and automatically reversed each period.",
          "When the real bill arrives, Lottie smart-matches it and releases the held reversal for you.",
          "Edit or stop a recurring accrual any time if the arrangement changes.",
        ],
        tip: "Recurring accruals keep month-end consistent — nothing is missed because a bill arrived late.",
        detail: {
          walkthrough: [
            "Lottie watches repeating bills and suggests recurring accruals automatically. Review each suggestion's amount and frequency before confirming.",
            "Once confirmed, the accrual is posted and reversed each period. When the real bill arrives, Lottie smart-matches it and releases the held reversal — you'll see the variance against the estimate.",
          ],
          gotchas: [
            "If a supplier's amount changes, edit the recurring accrual so the estimate stays accurate.",
          ],
        },
      },
      {
        icon: ShieldCheck, title: "Approve and post journals", to: "/approvals", cta: "Open Approvals",
        steps: [
          "Open Approvals to see journals waiting to be posted to Xero.",
          "Review the detail, then approve to post — or add a review note if something needs a second look.",
          "With two-person approval enabled (Settings → Approvals), a journal needs a second approver before it posts.",
          "Posted journals flow straight back into Xero with a clear narrative.",
        ],
        tip: "Every approval and posting is recorded in Audit History with full before/after detail.",
        detail: {
          walkthrough: [
            "Open Approvals to see journals waiting to post. Review the amount, date and narrative, then approve.",
            "With two-person approval on (Settings → Approvals), a second approver must sign off before a journal posts to Xero.",
            "To post everything that's due, use “Run daily posting now” from any register — it posts up to your open period's cut-off date.",
          ],
          gotchas: [
            "Journals dated after your open close-period cut-off are held until that period is reached.",
            "If a posting fails, the error is shown against the journal so you can fix and retry.",
          ],
        },
      },
      {
        icon: ChatCircleText, title: "Collaborate with review notes & @mentions", to: "/dashboard", cta: "Open Dashboard",
        steps: [
          "Open any schedule or pending journal and add a review note to raise a query.",
          "@mention a colleague to send it their way — they'll see it surfaced for them.",
          "Track all open queries from the queries panel on the Dashboard.",
          "Resolve a note once it's been actioned to keep the list clean.",
        ],
        tip: "Notes keep preparer and reviewer on the same page without leaving the app or emailing back and forth.",
        detail: {
          walkthrough: [
            "Add a review note on any schedule or pending journal to raise a query, and @mention a colleague to notify them.",
            "Track open queries from the Dashboard and resolve each one once it's been actioned.",
          ],
          gotchas: [
            "Read-only reviewers can add and resolve notes but can't change financial data — ideal for partner sign-off.",
          ],
        },
      },
      {
        icon: Lightbulb, title: "Find opportunities with Insights", to: "/insights", cta: "Open Insights",
        steps: [
          "Open Insights to see Lottie's suggestions across your registers.",
          "Review flagged items — missing schedules, unusual balances or items worth a second look.",
          "Action a suggestion directly, or dismiss it if it's expected.",
        ],
        tip: "Check Insights before close — it's a quick way to catch anything that slipped through.",
        detail: {
          walkthrough: [
            "Insights flags missing schedules, unusual balances and items worth a second look across every register.",
            "Action a suggestion directly, or dismiss it if it's expected and shouldn't show again.",
          ],
          gotchas: [
            "Run Insights just before close — it's the quickest catch-all for anything missed during the period.",
          ],
        },
      },
      {
        icon: Sparkle, title: "Explain a movement with AI", to: "/reconciliation", cta: "Open Reconciliation",
        steps: [
          "On any balance variance, or inside a schedule's detail drawer, click 'Explain'.",
          "Lottie writes a short, plain-English explanation of what the balance is and why it's moved.",
          "Copy it straight into your review notes or a client email.",
        ],
        tip: "Written in plain British English and ready to paste — perfect for review notes and client emails.",
        detail: {
          walkthrough: [
            "The 'Explain' button appears on balance-variance rows and in each schedule's detail drawer. Lottie reads the figures behind the movement and drafts a concise explanation you can drop into working papers or a client email.",
            "Explanations are cached, so re-opening the same unchanged figure is instant.",
          ],
          gotchas: [
            "Lottie only uses the figures in the schedule — always give the explanation a quick read before sending it to a client.",
          ],
        },
      },
    ],
  },
  {
    tag: "Month-end",
    guides: [
      {
        icon: Scales, title: "Reconcile to your Xero balances", to: "/reconciliation", cta: "Open Reconciliation",
        steps: [
          "Open Reconciliation to compare each register balance with the live balance in Xero.",
          "Where they tie, you're done. Where there's a variance, open it to see the difference.",
          "Either fix it (post an adjusting journal) or accept the variance with a reason for the audit trail.",
          "Re-check until every code reconciles cleanly.",
        ],
        tip: "Variances you accept are logged with their reason, so there's always a clear explanation at audit.",
        detail: {
          walkthrough: [
            "Reconciliation compares each register balance to the live balance in Xero, code by code.",
            "Where they tie, you're done. Where they don't, open the variance to see the difference, then post an adjusting journal or accept it with a reason.",
          ],
          gotchas: [
            "Reconciliation reads live from Xero, so post your journals first for an accurate comparison.",
            "Accepted variances are logged with their reason — there's always an explanation at audit.",
          ],
        },
      },
      {
        icon: Target, title: "Check accuracy and variances", to: "/accrual-accuracy", cta: "Open Accuracy",
        steps: [
          "Open the accuracy pages under Intelligence: Accrual, Prepayment and Deferred Income Accuracy.",
          "See where expected balances differ from the actual Xero position, broken down by category.",
          "Drill into a variance to understand the cause.",
          "Fix it, or 'leave as is' to accept — attention badges in the sidebar clear as you work through them.",
        ],
        tip: "These pages are the fastest way to prove your registers tie back to the ledger before sign-off.",
        detail: {
          walkthrough: [
            "The Accuracy pages (under Intelligence) show expected vs actual by category — accruals, prepayments and deferred income.",
            "Drill into a variance to find the cause, then fix it or “leave as is”. Sidebar attention badges clear as you resolve them.",
          ],
          gotchas: [
            "A small rounding variance is normal; a larger gap usually means a missing or mis-dated schedule.",
          ],
        },
      },
      {
        icon: Coins, title: "Manage deferred income & revenue recognition", to: "/deferred-reports", cta: "Open Revenue Recognition",
        steps: [
          "Open Deferred Income to see income invoiced in advance and how it's being recognised.",
          "Confirm or adjust the recognition basis for each schedule (e.g. straight-line over the contract).",
          "Open Revenue Recognition reports to view recognised vs deferred amounts by period.",
          "Export for management or audit.",
        ],
        tip: "Lottie releases deferred income to the P&L automatically over the period it's earned.",
        detail: {
          walkthrough: [
            "Deferred Income lists income invoiced in advance and how it's being recognised.",
            "Confirm or adjust the recognition basis (e.g. straight-line over the contract), then view recognised vs deferred by period in the Revenue Recognition reports and export.",
          ],
          gotchas: [
            "Lottie releases deferred income to the P&L automatically over the period it's earned — you don't post it by hand.",
          ],
        },
      },
      {
        icon: SealCheck, title: "Close the month and sign off", to: "/close", cta: "Open Month-end Close",
        steps: [
          "Open Month-end Close once journals are posted and balances reconciled.",
          "Work through the checklist — any outstanding items are flagged before you can sign off.",
          "Lock the period and generate a signed-off close pack for your records and reviewers.",
          "Reopen a period only if needed; every change stays in the audit trail.",
        ],
        tip: "Locking a period prevents further changes, giving you a clean, auditable cut-off.",
        detail: {
          walkthrough: [
            "Open Month-end Close once journals are posted and balances reconciled. Work through the readiness checklist — outstanding items are flagged before you can sign off.",
            "Lock the period and generate a signed-off close pack. Your progress is saved as a draft, so you can leave and return without losing anything.",
          ],
          gotchas: [
            "Locking prevents further changes for a clean cut-off. You can reopen a period if needed — every change stays in the audit trail.",
          ],
        },
      },
      {
        icon: Files, title: "Run reports and exports", to: "/reports", cta: "Open Reports",
        steps: [
          "Open Reports and choose a category (accruals, prepayments, deferred income or depreciation).",
          "Pick a register, forecast or balance report.",
          "Export to branded PDF or Excel — your practice logo appears automatically if set in Settings.",
        ],
        tip: "Add your practice logo under Settings → Branding so every export is client-ready.",
        detail: {
          walkthrough: [
            "Reports lets you pick a category and then a register, forecast or balance report.",
            "Export to branded PDF or Excel — your logo appears automatically if set. Registers are ordered by soonest next-journal date, matching the on-screen view.",
          ],
          gotchas: [
            "Set your logo under Settings → Branding first so every export is client-ready.",
          ],
        },
      },
      {
        icon: ChartLineUp, title: "Forecast the next 12 months", to: "/forecast", cta: "Open Forecast",
        steps: [
          "Open 12-Month Forecast to see opening balances, monthly releases and closing positions ahead.",
          "Filter by category to focus on accruals, prepayments or deferred income.",
          "Export the forecast for board or management reporting.",
        ],
        detail: {
          walkthrough: [
            "12-Month Forecast shows opening balances, monthly releases and closing positions for the year ahead.",
            "Filter by category and export for board or management reporting.",
          ],
          gotchas: [
            "The forecast starts from the month you select — snap it to the start of your reporting period.",
          ],
        },
      },
    ],
  },
  {
    tag: "Administration",
    guides: [
      {
        icon: ChartPieSlice, title: "Work across multiple organisations", to: "/portfolio", cta: "Open Clients",
        steps: [
          "Open Clients to see every connected organisation in one portfolio view, with key figures at a glance.",
          "Click an organisation to work in it, or use the organisation selector at the top of the sidebar to switch quickly.",
          "Each organisation keeps its own codes, schedules and settings — completely isolated.",
        ],
        tip: "The portfolio view is the fastest way to spot which clients need attention this month.",
        detail: {
          walkthrough: [
            "Clients shows every connected organisation with key figures. Click one to work in it, or use the sidebar selector to switch quickly.",
          ],
          gotchas: [
            "Each organisation is fully isolated — codes, schedules and settings never mix between clients.",
          ],
        },
      },
      {
        icon: UsersThree, title: "Manage your team", to: "/practice?tab=team", cta: "Open Team settings",
        steps: [
          "Go to Settings → Team & Users to invite colleagues.",
          "Assign a role: admins manage everything, while viewers are read-only and can leave review notes but never change financial data.",
          "Remove or change access at any time.",
        ],
        tip: "Read-only reviewer access is perfect for partners who sign off but don't prepare.",
        detail: {
          walkthrough: [
            "Settings → Team & Users lets you invite colleagues and assign a role: admin (full access) or viewer (read-only).",
            "Invited users set their own password from an email link and are forced to choose a new one on first login.",
          ],
          gotchas: [
            "You can't deactivate or demote your own account — ask another admin to do it.",
          ],
        },
      },
      {
        icon: Image, title: "Manage demo data", to: "/settings?tab=connect", cta: "Open Connections",
        steps: [
          "If you're trialling Lottie, demo data may be loaded so you can explore with realistic figures.",
          "An orange banner appears across the top while demo data is active.",
          "When you're ready to go live, clear the demo data from Settings → Connect to Xero before connecting your real organisation.",
        ],
        tip: "Always clear demo data before posting anything to a live Xero organisation.",
        detail: {
          walkthrough: [
            "During a trial, demo data lets you explore Lottie with realistic figures; an orange banner shows while it's active.",
            "When you're ready to go live, clear the demo data from Settings → Connect to Xero, then connect your real organisation.",
          ],
          gotchas: [
            "Always clear demo data before posting anything to a live Xero organisation.",
          ],
        },
      },
      {
        icon: Gauge, title: "Steer every client from the Command Centre", to: "/command-centre", cta: "Open Command Centre",
        steps: [
          "Open Command Centre for a live, practice-wide roll-up across every connected client.",
          "Scan the headline figures — total exposure, clients needing attention, overdue journals, missing accruals and month-ends still to sign off.",
          "Watch the 6-month exposure trend (stacked by client, with a ▲/▼ % badge) to see whether the book is growing or unwinding.",
          "Work the 'Needs attention' list top-down — it's sorted by urgency, with one-tap links into each client's Close, Reconciliation, Approvals or Recurring Accruals.",
          "Use 'My clients' to see just your book, or click a client (in the trend legend or worklist) to focus the whole cockpit on them.",
        ],
        tip: "Command Centre is the default home page for practices with more than one client — start your day here.",
        detail: {
          walkthrough: [
            "The Command Centre rolls every client into one cockpit: exposure by type, a 6-month exposure trend stacked by client, month-end sign-off status, overdue and due journals, unresolved variances, approvals waiting and likely missing accruals.",
            "The 'Needs attention' worklist ranks clients by urgency and links straight to the exact screen that needs action. A missing-accrual flag can be dismissed for the month once you've confirmed the bill genuinely isn't expected.",
            "Switch between 'All clients' and 'My clients', and click any client in the trend legend or worklist to focus every panel on that one client (clear the focus to go back).",
          ],
          gotchas: [
            "'Missing accruals' flags suppliers billed regularly in prior months but not yet this month — dismiss a flag if the bill genuinely isn't expected.",
            "Figures here are live and read-only — open a client to actually post or approve.",
          ],
        },
      },
      {
        icon: EnvelopeSimple, title: "Assign account managers & Monday briefings", to: "/portfolio", cta: "Open Clients",
        steps: [
          "On the Clients page, use the 'Account manager' dropdown on each client card to assign a colleague as its owner.",
          "In the Command Centre, set your Monday briefing to 'All clients', 'My clients only' or 'Off'.",
          "Use 'Send test' to email yourself a preview of the needs-attention briefing right now.",
        ],
        tip: "Assign an account manager to each client so the right person gets the Monday heads-up automatically.",
        detail: {
          walkthrough: [
            "Every Monday morning Lottie emails a 'needs attention' summary. Each client's assigned account manager receives their own clients; anyone who opts into 'All clients' receives the whole practice. Recipients with nothing to action are skipped.",
            "Assign or change an account manager any time from the Clients page (admins only). Each user controls their own briefing preference from the Command Centre.",
          ],
          gotchas: [
            "Only administrators can assign account managers. The briefing only sends to people who either manage a client or have opted in to 'All clients'.",
          ],
        },
      },
    ],
  },
];

// End-to-end journey — maps each phase to the guides above so a step can open & scroll to it.
export const JOURNEY = [
  {
    phase: "Set up once", icon: Rocket,
    blurb: "A few minutes to connect and configure — you only do this per client.",
    steps: [
      { label: "Connect Xero", tag: "Getting set up", i: 0 },
      { label: "Map your codes", tag: "Getting set up", i: 1 },
      { label: "Brand your reports", tag: "Getting set up", i: 2 },
    ],
  },
  {
    phase: "Throughout the period", icon: ArrowsClockwise,
    blurb: "Your regular rhythm — import, schedule, and keep journals flowing.",
    steps: [
      { label: "Import & schedule", tag: "Day-to-day", i: 0 },
      { label: "Recurring accruals", tag: "Day-to-day", i: 2 },
      { label: "Approve & post", tag: "Day-to-day", i: 3 },
      { label: "Collaborate & review", tag: "Day-to-day", i: 4 },
    ],
  },
  {
    phase: "At month-end", icon: CalendarCheck,
    blurb: "Tie back to Xero, close the period, and report with confidence.",
    steps: [
      { label: "Command Centre", tag: "Administration", i: 3 },
      { label: "Reconcile to Xero", tag: "Month-end", i: 0 },
      { label: "Check accuracy", tag: "Month-end", i: 1 },
      { label: "Deferred income", tag: "Month-end", i: 2 },
      { label: "Close the month", tag: "Month-end", i: 3 },
      { label: "Report & forecast", tag: "Month-end", i: 4 },
    ],
  },
];

// Total number of how-to guides (used by the Learning dashboard for progress %).
export const TOTAL_GUIDES = SECTIONS.reduce((n, s) => n + s.guides.length, 0);

// Flat list with stable anchors ("<tag>-<index>") for lookups.
export const FLAT_GUIDES = SECTIONS.flatMap((s) =>
  s.guides.map((g, i) => ({ ...g, tag: s.tag, i, anchor: `${s.tag}-${i}` })));

// App route → most relevant guide anchor (for the context-sensitive Help Drawer).
export const ROUTE_ANCHOR = {
  "/portfolio": "Administration-0",
  "/command-centre": "Administration-3",
  "/dashboard": "Day-to-day-0",
  "/unscheduled": "Day-to-day-0",
  "/accruals": "Day-to-day-1",
  "/prepayments": "Day-to-day-1",
  "/deferred-income": "Month-end-2",
  "/fixed-assets": "Day-to-day-1",
  "/recurring-accruals": "Day-to-day-2",
  "/treatment-rules": "Getting set up-1",
  "/approvals": "Day-to-day-3",
  "/insights": "Day-to-day-5",
  "/reconciliation": "Month-end-0",
  "/accrual-accuracy": "Month-end-1",
  "/prepayment-variance": "Month-end-1",
  "/deferred-income-variance": "Month-end-1",
  "/deferred-reports": "Month-end-2",
  "/close": "Month-end-3",
  "/reports": "Month-end-4",
  "/forecast": "Month-end-5",
  "/settings": "Getting set up-0",
  "/practice": "Administration-1",
};

// App route → the most relevant guided tour id (see lib/tours.js).
export const TOUR_FOR_ROUTE = {
  "/settings": "connect-xero",
  "/accruals": "create-accrual",
  "/prepayments": "create-accrual",
  "/unscheduled": "create-accrual",
  "/recurring-accruals": "create-accrual",
};

export function guideByAnchor(anchor) {
  return FLAT_GUIDES.find((g) => g.anchor === anchor) || null;
}

export function guideForRoute(route) {
  return guideByAnchor(ROUTE_ANCHOR[route] || "");
}

// Short, app-usage FAQs surfaced in the Help Drawer, tagged to the routes they help on.
export const FAQS = [
  { q: "Will anything post to Xero without my approval?", a: "No. You stay in control — journals can be held behind approval, and nothing posts to Xero until you (or a second approver) sign it off.", routes: ["/approvals", "/dashboard", "/accruals", "/prepayments"] },
  { q: "Why is nothing showing in Unscheduled Items?", a: "Only transactions posted to your mapped nominal codes appear. Check the code is mapped under Treatment Rules, then re-run Import from Xero.", routes: ["/unscheduled", "/treatment-rules"] },
  { q: "How do I fix a balance that doesn't match Xero?", a: "Open Reconciliation to compare code by code, then either post an adjusting journal or accept the variance with a reason for the audit trail.", routes: ["/reconciliation", "/accrual-accuracy", "/prepayment-variance", "/deferred-income-variance"] },
  { q: "Can I undo a journal I already posted?", a: "Yes — open the schedule and choose Reverse in Xero. Lottie posts a reversing journal and keeps the full history.", routes: ["/accruals", "/prepayments", "/deferred-income"] },
  { q: "How do I switch between organisations?", a: "Use the organisation selector at the top of the sidebar, or open Clients and pick an organisation to work in.", routes: ["/portfolio"] },
  { q: "How do I export a report?", a: "Open Reports, choose a category and report, then export to branded PDF or Excel. Your practice logo appears automatically if set.", routes: ["/reports", "/forecast"] },
  { q: "How does Lottie predict recurring accruals?", a: "She spots suppliers billed in previous months but missing this month and predicts the amount from a recency-weighted average, then auto-reverses next period.", routes: ["/recurring-accruals"] },
  { q: "Do I need to enter my Xero password anywhere?", a: "Never in Lottie. You authorise through Xero's own secure login — Lottie never sees or stores your Xero password.", routes: ["/settings"] },
  { q: "What is the Command Centre?", a: "It's a practice-wide cockpit that rolls up every connected client — total exposure, overdue journals, unresolved variances, missing accruals and month-end status — with a 'needs attention' worklist sorted by urgency and a 6-month exposure trend.", routes: ["/command-centre"] },
  { q: "How do I get the Monday briefing email?", a: "Assign yourself as a client's account manager on the Clients page, or set your briefing to 'All clients' in the Command Centre. Use 'Send test' to preview it now.", routes: ["/command-centre"] },
  { q: "How does Lottie flag a missing accrual?", a: "She spots suppliers billed regularly in prior months but not yet this month and flags them on the Command Centre. Dismiss a flag for the month once you've confirmed the bill isn't expected.", routes: ["/command-centre", "/recurring-accruals"] },
  { q: "Can Lottie explain a variance in plain English?", a: "Yes — click 'Explain' on any balance variance or in a schedule's detail drawer and Lottie drafts a short explanation you can paste into review notes or a client email.", routes: ["/reconciliation", "/accrual-accuracy", "/prepayment-variance", "/deferred-income-variance"] },
];

const GENERAL_FAQS = [FAQS[0], FAQS[5], FAQS[7]];

export function faqsForRoute(route) {
  const hits = FAQS.filter((f) => f.routes.includes(route));
  return (hits.length ? hits : GENERAL_FAQS).slice(0, 3);
}
